import { useCallback, useEffect, useRef, useState } from "react";

/* Narration engine — one continuous track, cue-driven captions.

   Why not Web Audio: on a phone, audio from an AudioContext plays on the RINGER channel. The
   iPhone's side switch silences it, and the video looks like it is playing with no sound at all —
   which is exactly what happened. A plain media element plays on the MEDIA channel, the one the
   volume buttons control, so it behaves like every other video the person watches on that phone.

   Why one file instead of 63 clips: WebKit will not let a media element start a NEW resource
   outside a user gesture, so a film assembled from per-line clips stops partway through on iOS.
   One resource, unlocked once by the press of play, runs to the end.

   Captions cannot drift because the cue timestamps are measured from this exact file at build time
   (scripts/build_tour.py writes each line's start/end from the same clock it assembles the master
   with), so a caption is simply "the line the playhead is inside".

   Two things this hook is deliberately careful about, because both showed up as "the video just
   cut out":
   1. It does NOT push state 60 times a second. The clock is published at 5Hz and the cue index and
      speaking flag only when they actually change — a four minute film used to mean ~14,000
      re-renders of a heavy animated tree, which is what starved phones of memory.
   2. A media element can lose its resource at any time (cellular drop, tab backgrounded, the OS
      reclaiming memory). currentTime then reads 0 and playback silently dies. Every failure mode
      is caught and the track is reloaded and resumed from the exact second it stopped at. */

const TICK = 0.06;         // clock publishes ~16x/sec: smooth motion, still ~4x fewer renders
const STALL_AFTER = 5000;  // playing but the playhead has not moved for this long = recover
/* The offline render steps the clock frame by frame and screenshots each step. Publishing the
   clock in 60ms buckets means two consecutive 30fps frames often read the SAME instant, so the
   render came out with runs of identical frames followed by a jump — judder that reads as the film
   "glitching", worst of all on a phone. In render mode the clock is published every frame. */
const renderMode = () => typeof window !== "undefined" && window.__TOUR_RENDER__ === true;

export function useNarration(lines, { onEnded, master, duration } = {}) {
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [recovering, setRecovering] = useState(false);

  const el = useRef(null);
  const raf = useRef(0);
  const vol = useRef(1);
  const total = useRef(0);
  const at = useRef(0);          // last good playhead position, survives a lost resource
  const wanted = useRef(false);  // the viewer pressed play and has not pressed pause
  const shown = useRef({ t: -1, i: -1, speaking: null });
  const moved = useRef(Date.now());
  const busy = useRef(false);
  const retry = useRef(0);
  const endedCb = useRef(onEnded);
  endedCb.current = onEnded;
  const masterRef = useRef(master);
  masterRef.current = master;

  useEffect(() => {
    if (!lines?.length) return;
    const last = lines[lines.length - 1];
    total.current = duration || last.end
      || lines.reduce((s, l) => s + l.secs + l.pause, 0);
    if (!el.current) {
      const a = new Audio();
      a.preload = "auto";
      a.playsInline = true;
      a.setAttribute("playsinline", "");
      a.setAttribute("data-testid", "tour-audio");
      a.volume = vol.current;
      // In the document, not floating: phone browsers give a real media element the media channel,
      // the lock-screen controls and the volume buttons.
      a.style.display = "none";
      document.body.appendChild(a);
      el.current = a;
    }
    if (master && !el.current.src) {
      el.current.src = master;
      el.current.load();
    }
  }, [lines, master]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Reload the track and pick up exactly where it stopped. Used for every way a media resource can
     go away: a network error, the browser emptying it, or the playhead simply freezing. */
  const recover = useCallback(() => {
    const a = el.current;
    const src = masterRef.current;
    if (!a || !src || busy.current) return;
    // The offline render pauses the element on purpose and drives currentTime itself. Nothing is
    // broken, so no recovery and — more to the point — no "reconnecting" badge, which was being
    // burnt into the middle of the film.
    if (renderMode()) return;
    busy.current = true;
    setRecovering(true);
    const resume = Math.max(0, Math.min(at.current, Math.max(total.current - 0.3, 0)));
    let settled = false;
    const done = (ok) => {
      if (settled) return;
      settled = true;
      clearTimeout(giveUp);
      a.removeEventListener("loadeddata", ready);
      a.removeEventListener("error", failed);
      busy.current = false;
      if (ok) {
        try { a.currentTime = resume; } catch { /* not seekable yet — start of track is fine */ }
        setRecovering(false);
        moved.current = Date.now();
        if (wanted.current) a.play().catch(() => {});
        return;
      }
      // The connection is still down. Keep the reconnecting state up and keep trying: an outage
      // that outlasts one attempt must not leave the film dead on the screen.
      retry.current = setTimeout(() => { if (wanted.current) recoverRef.current(); }, 2500);
    };
    const ready = () => done(true);
    const failed = () => done(false);
    const giveUp = setTimeout(() => done(false), 12000);
    a.addEventListener("loadeddata", ready);
    a.addEventListener("error", failed);
    try {
      a.src = `${src}${src.includes("?") ? "&" : "?"}r=${Date.now()}`;
      a.load();
    } catch {
      done(false);
    }
  }, []);
  const recoverRef = useRef(recover);
  recoverRef.current = recover;

  /* One loop: the clock, which line the playhead is inside, and a stall watchdog. */
  useEffect(() => {
    if (!lines?.length) return;
    const frame = () => {
      raf.current = requestAnimationFrame(frame);
      const a = el.current;
      if (!a) return;
      const t = a.currentTime || 0;
      // A missing or reloading resource reports currentTime 0. Treating that as a real playhead
      // move wiped out the position we need in order to resume, so only trust the clock when the
      // resource is actually there. A backward jump is only ever a seek, which sets at.current
      // itself.
      const trustworthy = !busy.current && !a.error && a.readyState >= 2
        && (t > 0.05 || at.current < 0.05);
      if (renderMode()) {
        // The render drives currentTime itself, one exact instant per frame. The element must be
        // PAUSED for that: while it was still playing, real time kept advancing during the wait
        // for the paint, so each captured frame landed 30-39ms apart instead of exactly 33.3ms.
        // Played back at a constant rate that irregular sampling is judder — the "glitching" —
        // and it also meant the render's paint handshake could never match, which crawled the
        // capture to a third of its speed.
        if (!a.paused) a.pause();
        at.current = Math.min(t, total.current);
      } else if (trustworthy && Math.abs(t - at.current) > 0.01) {
        at.current = Math.min(t, total.current);
        moved.current = Date.now();
      } else if (wanted.current && !a.paused && !busy.current
                 && Date.now() - moved.current > STALL_AFTER) {
        recover();
        return;
      }

      const tick = Math.round(at.current / TICK);
      if (renderMode()) {
        if (at.current !== shown.current.t) {
          shown.current.t = at.current;
          setElapsed(at.current);
        }
      } else if (tick !== shown.current.t) {
        shown.current.t = tick;
        setElapsed(at.current);
      }
      let i = 0;
      for (let k = 0; k < lines.length; k += 1) {
        if (at.current >= (lines[k].start ?? 0)) i = k;
        else break;
      }
      if (i !== shown.current.i) {
        shown.current.i = i;
        setIndex(i);
      }
      const line = lines[i];
      const talking = !!line && at.current >= (line.start ?? 0)
        && at.current <= (line.end ?? 0) + 0.08;
      if (talking !== shown.current.speaking) {
        shown.current.speaking = talking;
        setSpeaking(talking);
      }
    };
    raf.current = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf.current);
  }, [lines, recover]);

  useEffect(() => {
    const a = el.current;
    if (!a) return;
    const onEnd = () => {
      wanted.current = false;
      setPlaying(false);
      setSpeaking(false);
      at.current = total.current;
      setElapsed(total.current);
      endedCb.current?.();
    };
    // A pause the viewer did not ask for (resource pulled, tab throttled) is a failure, not a pause.
    const onPause = () => {
      if (wanted.current && !busy.current && at.current < total.current - 0.5) {
        recover();
        return;
      }
      setPlaying(false);
    };
    const onPlay = () => { moved.current = Date.now(); setPlaying(true); };
    const onFail = () => { if (wanted.current) recover(); };
    a.addEventListener("ended", onEnd);
    a.addEventListener("pause", onPause);
    a.addEventListener("playing", onPlay);
    a.addEventListener("error", onFail);
    a.addEventListener("emptied", onFail);
    a.addEventListener("stalled", onFail);
    a.addEventListener("abort", onFail);
    return () => {
      a.removeEventListener("ended", onEnd);
      a.removeEventListener("pause", onPause);
      a.removeEventListener("playing", onPlay);
      a.removeEventListener("error", onFail);
      a.removeEventListener("emptied", onFail);
      a.removeEventListener("stalled", onFail);
      a.removeEventListener("abort", onFail);
    };
  }, [lines, recover]);

  /* Coming back to a backgrounded tab is the other moment a phone has quietly dropped the audio. */
  useEffect(() => {
    const check = () => {
      const a = el.current;
      if (!a || document.hidden || !wanted.current || busy.current) return;
      if (a.paused || a.error || a.readyState === 0) recover();
    };
    document.addEventListener("visibilitychange", check);
    return () => document.removeEventListener("visibilitychange", check);
  }, [recover]);

  const play = useCallback(() => {
    const a = el.current;
    if (!a) return;
    if (!a.src && master) {
      a.src = master;
      a.load();
    }
    if (a.currentTime >= total.current - 0.1) {
      try { a.currentTime = 0; } catch { /* not seekable yet */ }
      at.current = 0;
    }
    wanted.current = true;
    moved.current = Date.now();
    setPlaying(true);
    a.play().catch(() => setPlaying(false));
  }, [master]);

  const pause = useCallback(() => {
    wanted.current = false;
    clearTimeout(retry.current);
    setRecovering(false);
    el.current?.pause();
    setPlaying(false);
    setSpeaking(false);
  }, []);

  const seek = useCallback((seconds) => {
    const a = el.current;
    if (!a) return;
    const to = Math.max(0, Math.min(seconds, total.current - 0.05));
    at.current = to;
    moved.current = Date.now();
    try { a.currentTime = to; } catch { /* not seekable yet */ }
    setElapsed(to);
  }, []);

  const setVolume = useCallback((v) => {
    vol.current = v;
    if (el.current) el.current.volume = v;
  }, []);

  useEffect(() => () => {
    cancelAnimationFrame(raf.current);
    clearTimeout(retry.current);
    wanted.current = false;
    if (el.current) {
      el.current.pause();
      el.current.src = "";
      el.current.remove();
      el.current = null;
    }
  }, []);

  return { index, playing, speaking, elapsed, total: total.current, recovering,
           play, pause, seek, setVolume };
}
